import React, { useState } from "react";
import {
    DndContext,
    DragEndEvent,
    DragOverlay,
    PointerSensor,
    useSensor,
    useSensors,
} from "@dnd-kit/core";

import TicketForm from "./TicketForm";
import type { Ticket } from "./types";
import "./Ticket.css";

import Modal from "./components/Modal";
import TicketsListPanel from "./components/TicketsListPanel";
import TargetsPanel from "./components/TargetsPanel";
import TicketDraggableCard from "./components/TicketDraggableCard";
import { useTicketsBoard } from "./hooks/useTicketsBoard";
import ToastHost from "./components/ToastHost";
import { useToasts } from "./hooks/useToasts";
import { useConfirm } from "./hooks/useConfirm";
import HeroSectionV2 from "../../../components/HeroSectionV2";
import Billede1 from "../../../Images/Billlede-1.png";

// NYT:
import { canAssignTicketToShift, getShiftTicketMismatchReason } from "./utils/ticketRules";

const TicketsBoardPage: React.FC = () => {
    const toast = useToasts();
    const { confirm, ConfirmUI } = useConfirm();

    const {
        filteredTickets,
        selectedTicket,
        setSelectedTicket,
        ticketSearch,
        setTicketSearch,
        showActiveOnly,
        setShowActiveOnly,
        hideExpired,
        setHideExpired,

        tab,
        setTab,

        peopleLoading,
        peopleError,
        personSearch,
        setPersonSearch,
        filteredPeople,
        personChipsById,

        events,
        eventsLoading,
        selectedEventId,
        setSelectedEventId,
        shifts,
        shiftsLoading,
        refreshShifts,
        shiftChipsById,

        functionsLoading,
        functionsError,
        filteredFunctions,
        functionSearch,
        setFunctionSearch,
        functionChipsById,
        refreshFunctions,

        onCreateTicket,
        onSaveTicket,
        onDeleteTicket,
        assignTicketTo,
    } = useTicketsBoard({ confirm, toast });

    const [activeDragTicket, setActiveDragTicket] = useState<Ticket | null>(null);

    const sensors = useSensors(
        useSensor(PointerSensor, {
            activationConstraint: { distance: 5 },
        })
    );

    const onDragStart = (event: any) => {
        const t: Ticket | undefined = event?.active?.data?.current?.ticket;
        setActiveDragTicket(t ?? null);
    };

    const onDragEnd = async (event: DragEndEvent) => {
        const ticket: Ticket | undefined = event.active.data.current?.ticket;
        setActiveDragTicket(null);

        if (!ticket?.ticketID) return;
        if (!event.over?.id) return;

        const overId = String(event.over.id);

        if (overId.startsWith("person-")) {
            const personID = Number(overId.replace("person-", ""));
            if (!Number.isFinite(personID) || personID <= 0) return;

            await assignTicketTo(ticket.ticketID, "person", personID);
            return;
        }

        if (overId.startsWith("shift-")) {
            const shiftID = Number(overId.replace("shift-", ""));
            if (!Number.isFinite(shiftID) || shiftID <= 0) return;

            const shift = shifts.find((s) => s.shiftsID === shiftID);
            if (!shift) return;

            if (!canAssignTicketToShift(ticket, shift)) {
                const reason =
                    getShiftTicketMismatchReason(ticket, shift) ??
                    "Ticket kan ikke tildeles denne vagt";

                toast.error(reason, "Ugyldig tildeling");

                return;
            }

            await assignTicketTo(ticket.ticketID, "shift", shiftID);
            return;
        }

        if (overId.startsWith("function-")) {
            const functionID = Number(overId.replace("function-", ""));
            if (!Number.isFinite(functionID) || functionID <= 0) return;

            await assignTicketTo(ticket.ticketID, "function", functionID);
            return;
        }
    };

    const onBulkCreateTickets = async (tickets: Ticket[]) => {
        for (const t of tickets) {
            await onSaveTicket(t);
        }

        toast.success(`${tickets.length} tickets oprettet`, "Bulk opret");
    };

    return (
        <div>
            <HeroSectionV2
                titleEn="Ticket"
                titleDa="Billetter"
                descriptionEn="Here you can create tickets and assign them"
                descriptionDa="Her kan du lave billetter og tildele dem"
                imageSrc={Billede1}
            />

            <div style={{ display: "grid", gridTemplateColumns: "420px 1fr", gap: 16 }}>
                <DndContext sensors={sensors} onDragStart={onDragStart} onDragEnd={onDragEnd}>
                    {/* LEFT */}
                    <TicketsListPanel
                        tickets={filteredTickets}
                        selectedTicket={selectedTicket}
                        onSelectTicket={setSelectedTicket}
                        onCreate={onCreateTicket}
                        onDelete={onDeleteTicket}
                        onBulkCreate={onBulkCreateTickets}
                        ticketSearch={ticketSearch}
                        setTicketSearch={setTicketSearch}
                        showActiveOnly={showActiveOnly}
                        setShowActiveOnly={setShowActiveOnly}
                        hideExpired={hideExpired}
                        setHideExpired={setHideExpired}
                    />

                    {/* RIGHT */}
                    <TargetsPanel
                        tab={tab}
                        setTab={setTab}
                        activeTicket={activeDragTicket ?? selectedTicket ?? null}
                        peopleLoading={peopleLoading}
                        peopleError={peopleError}
                        personSearch={personSearch}
                        setPersonSearch={setPersonSearch}
                        people={filteredPeople}
                        personChipsById={personChipsById}
                        events={events}
                        eventsLoading={eventsLoading}
                        selectedEventId={selectedEventId}
                        setSelectedEventId={setSelectedEventId}
                        shifts={shifts}
                        shiftsLoading={shiftsLoading}
                        refreshShifts={refreshShifts}
                        shiftChipsById={shiftChipsById}
                        functionsLoading={functionsLoading}
                        functionsError={functionsError}
                        refreshFunctions={refreshFunctions}
                        functionSearch={functionSearch}
                        setFunctionSearch={setFunctionSearch}
                        functions={filteredFunctions}
                        functionChipsById={functionChipsById}
                    />

                    {/* Drag overlay */}
                    <DragOverlay>
                        {activeDragTicket ? (
                            <div style={{ width: 360 }}>
                                <TicketDraggableCard
                                    ticket={activeDragTicket}
                                    selected={false}
                                    onSelect={() => { }}
                                />
                            </div>
                        ) : null}
                    </DragOverlay>

                    {/* Ticket Form Modal */}
                    <Modal
                        open={!!selectedTicket}
                        title={selectedTicket?.ticketID ? "Edit Ticket" : "Create Ticket"}
                        onClose={() => setSelectedTicket(null)}
                    >
                        {selectedTicket ? (
                            <TicketForm
                                ticket={selectedTicket}
                                onSave={onSaveTicket}
                                onCancel={() => setSelectedTicket(null)}
                            />
                        ) : null}
                    </Modal>

                    {ConfirmUI}
                    <ToastHost items={toast.toasts} onDismiss={toast.dismiss} />
                </DndContext>
            </div>
        </div>
    );
};

export default TicketsBoardPage;